import { useState, useEffect } from 'react';
import { getUserPermission, PERMISSIONS } from '../services/permissionService';

const PERMISSION_RANK = {
  [PERMISSIONS.VIEW_ONLY]: 1,
  [PERMISSIONS.LOG_ACCESS]: 2,
  [PERMISSIONS.SERVICE_ACCESS]: 3,
  [PERMISSIONS.FULL_ACCESS]: 4,
};

/**
 * Hook to compute the current user's permission level for a vehicle's garage.
 *
 * For owned garages (role !== 'collaborator'), returns FULL_ACCESS immediately
 * without any async Supabase call — the owner always has full access.
 *
 * For shared garages, calls getUserPermission() once on mount to fetch the
 * actual permission level from Supabase, then caches the result.
 *
 * @param {object} garage - Garage object from localStorage (has .role, .id)
 * @param {string} userId - Current auth user ID
 * @param {string} userEmail - Current auth user email
 * @returns {{ permissionLevel: string|null, loading: boolean, canLog: boolean, canService: boolean, canFull: boolean }}
 */
export function useVehiclePermissions(garage, userId, userEmail) {
  const isCollaborator = garage?.role === 'collaborator';
  const garageId = garage?.id;

  const [permissionLevel, setPermissionLevel] = useState(
    isCollaborator ? null : PERMISSIONS.FULL_ACCESS
  );
  const [loading, setLoading] = useState(isCollaborator);

  useEffect(() => {
    if (!isCollaborator) {
      setPermissionLevel(PERMISSIONS.FULL_ACCESS);
      setLoading(false);
      return;
    }
    if (!garageId || !userId) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    getUserPermission(garageId, userId, userEmail)
      .then((level) => {
        if (!cancelled) {
          // Default to VIEW_ONLY if permission is null (shouldn't happen for valid collaborators)
          setPermissionLevel(level || PERMISSIONS.VIEW_ONLY);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          // On error, be safe — restrict to VIEW_ONLY
          setPermissionLevel(PERMISSIONS.VIEW_ONLY);
          setLoading(false);
        }
      });

    return () => { cancelled = true; };
  }, [garageId, userId, userEmail, isCollaborator]);

  const rank = PERMISSION_RANK[permissionLevel] || 0;

  return {
    permissionLevel,
    loading,
    canLog: rank >= PERMISSION_RANK[PERMISSIONS.LOG_ACCESS],
    canService: rank >= PERMISSION_RANK[PERMISSIONS.SERVICE_ACCESS],
    canFull: rank >= PERMISSION_RANK[PERMISSIONS.FULL_ACCESS],
  };
}
